import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as ceremonyService from "../services/ceremonyService";
import * as eventService from "../services/eventService";
import CreateCeremonyModal from "../components/ceremonies/CreateCeremonyModal";
import EditCeremonyModal from "../components/ceremonies/EditCeremonyModal";

function etiquetaTipo(tipo: string) {
  if (tipo === "IGLESIA") return "Iglesia";
  if (tipo === "CIVIL") return "Civil";
  if (tipo === "RECEPCION") return "Recepción";
  return tipo;
}

function formatoHora(iso: string) {
  try {
    return new Intl.DateTimeFormat("es-PE", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatoFechaLarga(iso: string) {
  try {
    return new Intl.DateTimeFormat("es-PE", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function imagenPorTipo(tipo: string) {
  // Mantengo “estilo original” con imágenes similares a tu HTML.
  if (tipo === "CIVIL")
    return "https://lh3.googleusercontent.com/aida-public/AB6AXuCK9Rfr7A39Zj0yEHfSZgPtQJfM-bpTdVb2vojVuc_HnwPIoKYKR6Lo_COO2UgivzGfdKM3RyXqiBr1cnZWMhat4cKROWsmXAedPhiLKbj23Q9zjUjvlLE3psdQ-7z7uT2OOsaglQETENQuXl9gsIEyd1g6-i1uVJRU_OeKmG7Fsx5GMrqBcr8jnpPKpE56xEX9OoavrOut3uZFVkf08U_fzP5vM3TtHMBOFZKODgbrQY0kH3BkYZ4xP_XeJA1YIRzsmTOQFnZubZ2a";
  if (tipo === "IGLESIA")
    return "https://lh3.googleusercontent.com/aida-public/AB6AXuBTMuCb3IVgaREV6N_Pz6gZmRdUx9x6Hiy8XEbrjukrQptHSDZLnlLVtV5aQxoW-F2Vj04qTvs8-aZoCyIYG3Wm2JOca-j1FFtrYjKJd_qbiyQzVLn7q-CbDGgEkUGsJjOybWAV8b80Wn5gj9JZ0Ey5jn4IuqupFwTtKU8FPVhZF9LJYqghS18EsNRuhMTFD-SvNPgoNHr5oq_qc2ayw41QUaJpXB-Pu4Nr8bHKI5uR-CNFOnFE-tCDlD8TS1sqyvLj_SbuL3wahyEB";
  return "https://lh3.googleusercontent.com/aida-public/AB6AXuAY7DbWXF3li-TGheTjm_b60BDzG5TJlbumG_ijNH91pZKuhE4Ng-aTlisucUDgILujr0uAyvmPYvwd_JZlFnbkl_dkSPc5YAYb7IP70Iy1iin7PPful2ajfWEjEXthFcJuWFH6ZtkgxoUrDZ2NLKIfzo1W7jib-eK459i3KCYLklX9cJzD6hCwgC5dmSWyQMxS3y76yQHVjgNf_Dbn2yXfMFSiAPkqcVf2Y03pwTVN_w26vKT2436os-Ev-wKqcaYtRiGJ6aY3wSLO";
}

function CeremoniesPage() {
  const navigate = useNavigate();
  const { eventoId } = useParams();
  const idEvento = Number(eventoId);

  const [eventoNombre, setEventoNombre] = useState<string>("Evento");
  const [eventoFecha, setEventoFecha] = useState<string | null>(null);

  const [ceremonias, setCeremonias] = useState<ceremonyService.Ceremonia[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selected, setSelected] = useState<ceremonyService.Ceremonia | null>(null);

  async function cargar() {
    if (!Number.isFinite(idEvento)) return;

    setLoading(true);
    setErrorMsg(null);
    try {
      // (opcional) Traer el evento para mostrar nombre/fecha
      try {
        const ev = await eventService.obtenerEvento(idEvento);
        setEventoNombre(ev.nombre);
        setEventoFecha(ev.fechaPrincipal);
      } catch {
        // si falla, no bloquea ceremonias
      }

      const data = await ceremonyService.listarCeremonias(idEvento);
      // Ordenamos por "orden" si viene, si no por fecha
      const sorted = [...data].sort((a, b) => {
        const oa = a.orden ?? 9999;
        const ob = b.orden ?? 9999;
        if (oa !== ob) return oa - ob;
        return new Date(a.fechaHora).getTime() - new Date(b.fechaHora).getTime();
      });
      setCeremonias(sorted);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Error cargando ceremonias");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idEvento]);

  const filtradas = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return ceremonias;
    return ceremonias.filter((c) => {
      const t = (c.tipo ?? "").toLowerCase();
      const n = (c.nombre ?? "").toLowerCase();
      const l = (c.lugarNombre ?? "").toLowerCase();
      const d = (c.direccion ?? "").toLowerCase();
      return t.includes(query) || n.includes(query) || l.includes(query) || d.includes(query);
    });
  }, [ceremonias, q]);

  function abrirEditar(c: ceremonyService.Ceremonia) {
    setSelected(c);
    setOpenEdit(true);
  }

  async function eliminar(c: ceremonyService.Ceremonia) {
    const ok = window.confirm(
      `¿Seguro que deseas eliminar la ceremonia "${c.nombre ?? etiquetaTipo(c.tipo)}"?`
    );
    if (!ok) return;

    try {
      await ceremonyService.eliminarCeremonia(idEvento, c.id);
      setCeremonias((prev) => prev.filter((x) => x.id !== c.id));
    } catch (e) {
      alert(e instanceof Error ? e.message : "Error eliminando ceremonia");
    }
  }

  function onCreated(created: ceremonyService.Ceremonia) {
    setCeremonias((prev) => [created, ...prev]);
  }

  function onUpdated(updated: ceremonyService.Ceremonia) {
    setCeremonias((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
  }

  return (
    <>
      {/* Contenedor scrollable interno */}
      <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:px-12">
        <div className="max-w-6xl mx-auto flex flex-col gap-8">
          {/* Top: Breadcrumb + Heading */}
          <div className="flex flex-col gap-6">
            {/* Breadcrumbs + volver */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <button
                  type="button"
                  onClick={() => navigate("/home/eventos")}
                  className="text-[#637588] dark:text-[#ab9db8] font-medium hover:text-primary transition-colors flex items-center gap-1"
                >
                  <span className="material-symbols-outlined text-base">arrow_back</span>
                  Eventos
                </button>

                <span className="material-symbols-outlined text-[#637588] dark:text-[#ab9db8] text-base">
                  chevron_right
                </span>

                <span className="text-[#111418] dark:text-white font-semibold">
                  Ceremonias
                </span>
              </div>

              <button
                type="button"
                onClick={() => setOpenCreate(true)}
                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white px-5 py-2.5 rounded-lg font-bold shadow-lg shadow-primary/20 transition-all active:scale-95 shrink-0"
              >
                <span className="material-symbols-outlined text-sm font-bold">add</span>
                <span>Agregar ceremonia</span>
              </button>
            </div>

            {/* Heading */}
            <div className="flex flex-col gap-2">
              <h1 className="text-[#111418] dark:text-white text-2xl sm:text-3xl md:text-4xl font-black leading-tight tracking-tight">
                Gestión de ceremonias:{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-400">
                  {eventoNombre}
                </span>
              </h1>
              <p className="text-[#637588] dark:text-[#ab9db8] text-base font-normal">
                Administra y programa las diferentes ceremonias para este evento.
              </p>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex flex-col gap-2 rounded-xl p-5 border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm">
              <div className="flex justify-between items-start">
                <p className="text-[#637588] dark:text-[#ab9db8] text-sm font-medium uppercase tracking-wider">
                  Total de ceremonias
                </p>
                <span className="material-symbols-outlined text-primary">event</span>
              </div>
              <p className="text-[#111418] dark:text-white text-3xl font-bold leading-tight">
                {ceremonias.length} programadas
              </p>
            </div>

            <div className="flex flex-col gap-2 rounded-xl p-5 border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm">
              <div className="flex justify-between items-start">
                <p className="text-[#637588] dark:text-[#ab9db8] text-sm font-medium uppercase tracking-wider">
                  Fecha del evento
                </p>
                <span className="material-symbols-outlined text-primary">calendar_month</span>
              </div>
              <p className="text-[#111418] dark:text-white text-3xl font-bold leading-tight">
                {eventoFecha ? formatoFechaLarga(eventoFecha) : "—"}
              </p>
            </div>

            <div className="flex flex-col gap-2 rounded-xl p-5 border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] shadow-sm">
              <div className="flex justify-between items-start">
                <p className="text-[#637588] dark:text-[#ab9db8] text-sm font-medium uppercase tracking-wider">
                  Progreso general
                </p>
                <span className="material-symbols-outlined text-primary">trending_up</span>
              </div>
              <div className="flex items-end gap-2">
                <p className="text-[#111418] dark:text-white text-3xl font-bold leading-tight">
                  —
                </p>
                <span className="text-green-500 text-sm font-medium mb-1.5">
                  En curso
                </span>
              </div>
              <div className="w-full bg-[#e0e0e0] dark:bg-[#362f40] h-1.5 rounded-full mt-1">
                <div className="bg-primary h-1.5 rounded-full" style={{ width: "40%" }} />
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white dark:bg-[#231b2e] p-2 rounded-xl border border-[#e0e0e0] dark:border-[#473c53]">
            <div className="relative w-full md:w-auto md:min-w-[300px]">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[#637588] dark:text-[#ab9db8]">
                search
              </span>
              <input
                className="w-full bg-[#f7f6f8] dark:bg-[#191121] text-[#111418] dark:text-white pl-10 pr-4 py-2 rounded-lg border-none focus:ring-2 focus:ring-primary placeholder-[#637588] dark:placeholder-[#645970] text-sm"
                placeholder="Buscar ceremonias..."
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>

            {/* Toggle visual (por ahora solo visual, dejamos List activo como en tu HTML) */}
            <div className="flex bg-[#f7f6f8] dark:bg-[#191121] p-1 rounded-lg w-full md:w-auto">
              <div className="cursor-pointer flex-1 md:flex-none px-4 py-1.5 rounded-md text-[#637588] dark:text-[#ab9db8] text-sm font-medium flex items-center justify-center gap-2">
                <span className="material-symbols-outlined text-[18px]">timeline</span>
                <span>Línea de tiempo</span>
              </div>
              <div className="cursor-pointer flex-1 md:flex-none px-4 py-1.5 rounded-md bg-white dark:bg-[#302938] text-primary dark:text-white shadow-sm text-sm font-medium flex items-center justify-center gap-2">
                <span className="material-symbols-outlined text-[18px]">view_list</span>
                <span>Lista</span>
              </div>
            </div>
          </div>

          {/* Estado */}
          {loading && (
            <div className="text-[#637588] dark:text-[#ab9db8]">Cargando ceremonias...</div>
          )}
          {!loading && errorMsg && <div className="text-red-400">{errorMsg}</div>}

          {/* Lista */}
          {!loading && !errorMsg && (
            <div className="flex flex-col gap-4 pb-10">
              {filtradas.map((c) => (
                <div
                  key={c.id}
                  className="group flex flex-col md:flex-row rounded-xl border border-[#e0e0e0] dark:border-[#473c53] bg-white dark:bg-[#231b2e] overflow-hidden hover:border-primary/50 transition-all shadow-sm hover:shadow-md"
                >
                  {/* Left: Image & Time */}
                  <div className="relative w-full md:w-48 h-32 md:h-auto shrink-0">
                    <div
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                      style={{ backgroundImage: `url("${imagenPorTipo(c.tipo)}")` }}
                    />
                    <div className="absolute inset-0 bg-black/40 md:bg-black/20 flex flex-col items-center justify-center md:items-start md:justify-end p-4 text-white">
                      <span className="text-xs font-semibold bg-primary px-2 py-0.5 rounded uppercase tracking-wider mb-1 md:mb-2">
                        {formatoHora(c.fechaHora)}
                      </span>
                    </div>
                  </div>

                  {/* Middle: Details */}
                  <div className="flex-1 p-5 flex flex-col justify-center gap-2">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-bold text-[#111418] dark:text-white">
                        {c.nombre ?? etiquetaTipo(c.tipo)}
                      </h3>
                      <span className="px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-bold border border-primary/20">
                        {etiquetaTipo(c.tipo)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-[#637588] dark:text-[#ab9db8] text-sm">
                      <span className="material-symbols-outlined text-[18px]">location_on</span>
                      <span>{c.lugarNombre ?? "—"}</span>
                    </div>

                    <div className="flex items-center gap-2 text-[#637588] dark:text-[#ab9db8] text-sm">
                      <span className="material-symbols-outlined text-[18px]">pin_drop</span>
                      <span className="truncate">{c.direccion ?? "—"}</span>
                    </div>

                    {c.urlMaps && (
                      <div className="flex items-center gap-2 text-[#637588] dark:text-[#ab9db8] text-sm">
                        <span className="material-symbols-outlined text-[18px]">map</span>
                        <a
                          className="text-primary hover:underline truncate"
                          href={c.urlMaps}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Ver en Google Maps
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Right: Actions */}
                  <div className="flex md:flex-col items-center md:items-end justify-between md:justify-center p-5 gap-4 border-t md:border-t-0 md:border-l border-[#e0e0e0] dark:border-[#362f40] bg-[#fcfcfc] dark:bg-[#2a2235]">
                    <div className="text-right">
                      <p className="text-xs text-[#637588] dark:text-[#ab9db8] font-medium uppercase tracking-wider">
                        Fecha
                      </p>
                      <p className="text-sm text-[#111418] dark:text-white font-semibold">
                        {formatoFechaLarga(c.fechaHora)}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => abrirEditar(c)}
                        className="p-2 rounded-lg text-[#637588] dark:text-[#ab9db8] hover:bg-[#e0e0e0] dark:hover:bg-[#3d3448] hover:text-primary transition-colors"
                        title="Editar"
                      >
                        <span className="material-symbols-outlined">edit</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => eliminar(c)}
                        className="p-2 rounded-lg text-[#637588] dark:text-[#ab9db8] hover:bg-[#e0e0e0] dark:hover:bg-[#3d3448] hover:text-red-500 transition-colors"
                        title="Eliminar"
                      >
                        <span className="material-symbols-outlined">delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Placeholder para agregar */}
              <button
                type="button"
                onClick={() => setOpenCreate(true)}
                className="flex flex-col items-center justify-center gap-2 p-8 rounded-xl border-2 border-dashed border-[#e0e0e0] dark:border-[#362f40] bg-transparent hover:bg-white dark:hover:bg-[#231b2e] hover:border-primary transition-all group"
              >
                <div className="size-12 rounded-full bg-[#f7f6f8] dark:bg-[#302938] flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors text-[#637588] dark:text-[#ab9db8]">
                  <span className="material-symbols-outlined">add</span>
                </div>
                <p className="text-[#637588] dark:text-[#ab9db8] font-medium group-hover:text-primary transition-colors">
                  Agregar una nueva ceremonia
                </p>
              </button>
            </div>
          )}
        </div>
      </div>

      <CreateCeremonyModal
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        eventoId={idEvento}
        onCreated={onCreated}
      />

      <EditCeremonyModal
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        eventoId={idEvento}
        ceremonia={selected}
        onUpdated={onUpdated}
      />
    </>
  );
}

export default CeremoniesPage;
